// Proposal Calculator Service Module
// Handles proposal-specific calculations using MAXtimate™ engine

import { type ShowWithDetails } from "@shared/schema";
import { type CalculatorResult, type ProposalCalculationResult } from '../types/calculator.types';
import { normalizeCareerStage } from '../utils/careerStage';
import { calculateWithAPI } from './calculatorApi';

// Calculate average DMA population for market size
function calculateAverageDMAPopulation(shows: ShowWithDetails[]): string {
  const dmaPopulations = shows
    .map(show => show.venue.market?.population || 0)
    .filter(pop => pop > 0);
  
  if (dmaPopulations.length === 0) return "Medium";
  
  const avgPopulation = dmaPopulations.reduce((sum, pop) => sum + pop, 0) / dmaPopulations.length;
  
  // Classify market size based on average DMA population
  if (avgPopulation >= 2000000) return "Large";
  if (avgPopulation >= 700000) return "Medium";
  return "Small";
}

export async function calculatePlanMetrics(
  shows: ShowWithDetails[],
  objective: string
): Promise<ProposalCalculationResult> {
  // Group shows by artist to get individual calculations
  const artistShowCounts = new Map<number, {
    artist: ShowWithDetails['artist'];
    shows: ShowWithDetails[];
    count: number;
  }>();
  
  shows.forEach(show => {
    const artistId = show.artist.id;
    if (!artistShowCounts.has(artistId)) {
      artistShowCounts.set(artistId, {
        artist: show.artist,
        shows: [show],
        count: 1
      });
    } else {
      const existing = artistShowCounts.get(artistId)!;
      existing.shows.push(show);
      existing.count++;
    }
  });
  
  // Calculate MAXtimate™ pricing for each artist using server API
  const artistCalculations: {
    artistId: number;
    artistName: string;
    calculation: CalculatorResult;
  }[] = [];
  
  let totalCostMin = 0;
  let totalCostMax = 0;
  let totalImpressionsMin = 0;
  let totalImpressionsMax = 0;
  let totalLeadsMin = 0;
  let totalLeadsMax = 0;
  
  // Count shows by career stage breakdown
  const careerStageBreakdown = {
    emerging: 0,
    breakthrough: 0,
    established: 0,
    mainstream: 0
  };
  
  // Process each unique artist
  for (const [artistId, artistData] of artistShowCounts) {
    const { artist, shows: artistShows, count } = artistData;
    
    // Calculate market size INDIVIDUALLY for this artist's shows
    const artistMarketSize = calculateAverageDMAPopulation(artistShows);
    console.log('DEBUG Market calculation:', {
      artist: artist.name,
      showCount: artistShows.length,
      populations: artistShows.map(s => s.venue.market?.population || 0),
      avgPopulation: artistShows.map(s => s.venue.market?.population || 0).reduce((a,b) => a+b, 0) / artistShows.length,
      resultingMarket: artistMarketSize
    });
    
    // Normalize career stage (handles Break-through/Established variants)
    const normalizedCareerStage = normalizeCareerStage(artist.careerStage || "Emerging");
    
    // Update career stage breakdown
    const stageKey = normalizedCareerStage.toLowerCase().split(' ')[0]; // Get base stage name
    if (stageKey === "emerging") careerStageBreakdown.emerging += count;
    else if (stageKey === "breakthrough") careerStageBreakdown.breakthrough += count;
    else if (stageKey === "established") careerStageBreakdown.established += count;
    else if (stageKey === "mainstream") careerStageBreakdown.mainstream += count;
    else careerStageBreakdown.emerging += count; // Default to emerging
    
    // Calculate average venue capacity for this artist's shows
    const averageVenueCapacity = artistShows.reduce((sum, show) => sum + (show.venue.capacity || 10000), 0) / artistShows.length;

    // Use server API for authentic calculation with media costs and venue capacity
    const apiParams = {
      careerStage: normalizedCareerStage,
      numberOfShows: count,
      mediaObjective: objective,
      marketSize: artistMarketSize, // Use individual artist's market size
      venueCapacity: Math.round(averageVenueCapacity)
    };
    
    console.log('DEBUG API Call params:', apiParams);
    
    try {
      const calculation = await calculateWithAPI(apiParams);

      if (calculation) {
        // Use API calculation results directly without MAXscore adjustments
        artistCalculations.push({
          artistId: artistId,
          artistName: artist.name,
          calculation
        });

        // Add totals from API calculation
        totalCostMin += calculation.estimatedCost.low;
        totalCostMax += calculation.estimatedCost.high;
        totalImpressionsMin += calculation.estimatedImpressions.low;
        totalImpressionsMax += calculation.estimatedImpressions.high;
        
        if (calculation.estimatedLeads) {
          totalLeadsMin += calculation.estimatedLeads.low;
          totalLeadsMax += calculation.estimatedLeads.high;
        }
      }
    } catch (error) {
      // Error handled by fallback calculation
      // Skip this artist if calculation fails
      continue;
    }
  }
  
  // Calculate leads for lead-focused campaigns if not already calculated
  let estimatedLeads: { min: number; max: number } | undefined;
  if (objective === "leads" && (totalLeadsMin > 0 || totalLeadsMax > 0)) {
    estimatedLeads = {
      min: Math.round(totalLeadsMin),
      max: Math.round(totalLeadsMax)
    };
  } else if (objective === "leads") {
    // Fallback calculation if MAXtimate™ doesn't provide leads
    const leadRate = 0.02; // 2% conversion rate
    estimatedLeads = {
      min: Math.round(totalImpressionsMin * leadRate),
      max: Math.round(totalImpressionsMax * leadRate)
    };
  }
  
  // Calculate aggregate breakdown from all artist calculations
  let totalShowImpressions = 0;
  let totalMediaImpressions = 0;
  let totalArtistCost = 0;
  let totalMediaCost = 0;
  
  for (const artistCalc of artistCalculations) {
    if (artistCalc.calculation.breakdown) {
      totalShowImpressions += artistCalc.calculation.breakdown.showImpressions;
      totalMediaImpressions += artistCalc.calculation.breakdown.mediaImpressions;
      totalArtistCost += artistCalc.calculation.breakdown.artistCost;
      totalMediaCost += artistCalc.calculation.breakdown.mediaCost;
    }
  }

  // Debug final values
  console.log('DEBUG Final Proposal Values:', {
    totalCostMin: Math.round(totalCostMin),
    totalCostMax: Math.round(totalCostMax)
  });
  
  return {
    estimatedCost: {
      min: Math.round(totalCostMin),
      max: Math.round(totalCostMax)
    },
    estimatedImpressions: {
      min: Math.round(totalImpressionsMin),
      max: Math.round(totalImpressionsMax)
    },
    estimatedLeads,
    careerStageBreakdown,
    breakdown: {
      showImpressions: totalShowImpressions,
      mediaImpressions: totalMediaImpressions,
      artistCost: totalArtistCost,
      mediaCost: totalMediaCost
    },
    artistCalculations
  };
}

// Marketing objectives from calculator
export function getMarketingObjectiveOptions() {
  const objectives = [
    { value: "awareness", label: "Awareness", cpm: 5, impressionMultiplier: 10 },
    { value: "engagement", label: "Engagement", cpm: 12, impressionMultiplier: 6 },
    { value: "leads", label: "Leads", cpm: 25, impressionMultiplier: 3 },
    { value: "no_media", label: "No Media", cpm: 0, impressionMultiplier: 1 }
  ];

  return objectives.map(obj => ({
    value: obj.value,
    label: obj.label
  }));
}